import React from 'react'
import { Switch, Route, RouteComponentProps, withRouter } from 'react-router'
import Redirect from '#src/components/Redirect'
import { NotFound } from '#src/components/Errors'
import { CartProvider } from '#src/containers/Cart'
import { PageKeyRoute, PageKeyPriority } from '@deal/page-key'

import ChatProvider from '#src/containers/Chat'
import * as Routes from './routes'

interface RouterProps extends RouteComponentProps<{}> {}

const Router: React.FC<RouterProps> = () => {
  return (
    <Switch>
      <Route path="/r" component={Routes.Redirects} />
      {/**
       * The authentication route requires special treament:
       *
       *   1) We don't want to initiate subscriptions that require authenticated state while
       *      logging out, or we'll hit race conditions between clearing authentication cookies
       *      and re-setting the subscription to no longer use the authenticated state.
       *
       *   2) The route should always be accessible, and not subject to the Deal.com => Curated.com
       *      redirect and splash screen.
       */}
      <PageKeyRoute
        path="/auth"
        pageKey="auth"
        priority={PageKeyPriority.TOP_LEVEL}
        component={Routes.Auth}
      />
      <PageKeyRoute
        path={['/lp', '/landing']}
        pageKey="landing"
        priority={PageKeyPriority.TOP_LEVEL}
        component={Routes.Landing}
      />
      <PageKeyRoute
        path="/checkout"
        pageKey="checkout"
        priority={PageKeyPriority.TOP_LEVEL}
        component={Routes.Checkout}
      />
      {/**
       * This is only for redirect purpose. we used to have /orders route for both checkout and order list/details.
       * The checkout is now at /checkout and order list/details is at /account/orders.
       * Keep this route for redirect purpose only.
       */}
      <PageKeyRoute
        path="/orders"
        pageKey="orders"
        priority={PageKeyPriority.TOP_LEVEL}
        component={Routes.Orders}
      />
      {/**
       * This is for guest accessible gift card redeem.  All gift card functionality for logged in users is under
       * /accounts/gift-cards
       */}
      <PageKeyRoute
        path="/gift-cards"
        pageKey="gift-cards"
        priority={PageKeyPriority.TOP_LEVEL}
        component={Routes.GiftCards}
      />
      <PageKeyRoute
        path="/path"
        pageKey="πath"
        priority={PageKeyPriority.TOP_LEVEL}
        component={Routes.Path}
      />
      <PageKeyRoute
        path="/requests"
        pageKey="requests"
        priority={PageKeyPriority.TOP_LEVEL}
        component={Routes.Requests}
      />
      <PageKeyRoute
        path="/referral"
        pageKey="referral"
        priority={PageKeyPriority.TOP_LEVEL}
        component={Routes.Referral}
      />
      <PageKeyRoute
        path="/sms-promo"
        pageKey="sms-promo"
        priority={PageKeyPriority.TOP_LEVEL}
        component={Routes.SmsPromo}
      />

      <Route
        path="/"
        render={() => (
          <CartProvider>
            <ChatProvider>
              <Switch>
                {/**
                 * These are 'deals' searches specifically targeted towards promotions.
                 */}
                <PageKeyRoute
                  path="/black-friday"
                  pageKey="black-friday-search"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.PromotionalSearch}
                />
                <PageKeyRoute
                  path="/cyber-monday"
                  pageKey="cyber-monday-search"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.PromotionalSearch}
                />
                <PageKeyRoute
                  path="/about-us"
                  pageKey="about-us"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.About}
                />
                <PageKeyRoute
                  path="/account"
                  pageKey="account"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Account}
                />
                <PageKeyRoute
                  path="/apply"
                  pageKey="apply"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Apply}
                />
                <PageKeyRoute
                  path="/careers"
                  pageKey="careers"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Careers}
                />

                {/*
                 * Due to an altered taxonomy, we're going to temporarily redirect the existing
                 * bike categories to their new location. I'll double back and clear these up
                 * in a week or so when the 301s have taken hold and been crawled. @ktsosno
                 */}
                <Route
                  path="/c/mountain-bikes"
                  exact
                  render={() => <Redirect to="/c/bikes/f-bike-type/mountain" statusCode={301} />}
                />
                <Route
                  path="/c/road-bikes"
                  exact
                  render={() => <Redirect to="/c/bikes/f-bike-type/road" statusCode={301} />}
                />
                <Route
                  path="/c/commuter-bikes"
                  exact
                  render={() => <Redirect to="/c/bikes/f-bike-type/commuter" statusCode={301} />}
                />
                <Route
                  path="/c/electric-bikes"
                  exact
                  render={() => <Redirect to="/c/bikes/f-bike-type/electric" statusCode={301} />}
                />

                <PageKeyRoute
                  path="/c"
                  pageKey="categories"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Categories}
                />
                <PageKeyRoute
                  path="/reviews"
                  pageKey="reviews"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Reviews}
                />
                <PageKeyRoute
                  path="/curations"
                  pageKey="curations"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Curations}
                />
                <PageKeyRoute
                  path="/d"
                  pageKey="departments"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Departments}
                />
                <PageKeyRoute
                  path="/experts"
                  pageKey="experts"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Experts}
                />
                <PageKeyRoute
                  path="/feedback"
                  pageKey="feedback"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Feedback}
                />
                <PageKeyRoute
                  path="/faq"
                  pageKey="faq"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.FAQ}
                />
                <PageKeyRoute
                  path="/favorites"
                  pageKey="favorites"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Favorites}
                />
                <PageKeyRoute
                  path="/goodbye-charters"
                  pageKey="goodbye-charters"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.GoodbyeCharters}
                />
                <PageKeyRoute
                  path="/guest-checkout"
                  pageKey="guest-checkout"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.GuestCheckout}
                />
                <PageKeyRoute
                  path="/guest-curation-view"
                  pageKey="guest-curation-view"
                  priority={PageKeyPriority.TOP_LEVEL}
                  render={() => <Redirect to="/goodbye-charters" />}
                />
                <PageKeyRoute
                  path="/how-it-works"
                  pageKey="how-it-works"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.HowItWorks}
                />
                <PageKeyRoute
                  path="/in-action"
                  pageKey="in-action"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.InAction}
                />
                <PageKeyRoute
                  path="/inbox"
                  pageKey="inbox"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Inbox}
                />
                <PageKeyRoute
                  path="/journal"
                  pageKey="journal"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Journal}
                />
                <PageKeyRoute
                  path="/legal"
                  pageKey="legal"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Legal}
                />
                <PageKeyRoute
                  path="/giveaways"
                  pageKey="giveaways"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Giveaways}
                />
                <PageKeyRoute
                  path="/meet-the-experts"
                  pageKey="meet-the-experts"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.MeetTheExperts}
                />
                <PageKeyRoute
                  path="/organic-referral-signup"
                  pageKey="organic-referral-signup"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.OrganicReferralSignup}
                />
                <PageKeyRoute
                  path="/refer-a-friend"
                  pageKey="refer-a-friend"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.ReferAFriend}
                />
                <PageKeyRoute
                  path="/returns"
                  pageKey="returns"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Returns}
                />
                <PageKeyRoute
                  path="/products"
                  pageKey="products"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Products}
                />
                <PageKeyRoute
                  path="/sellable"
                  pageKey="sellable"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Sellable}
                />
                <PageKeyRoute
                  path="/support"
                  pageKey="support"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Support}
                />
                <PageKeyRoute
                  path="/team"
                  pageKey="team"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.Team}
                />
                <PageKeyRoute
                  path="/most-recommended"
                  pageKey="most-recommended"
                  priority={PageKeyPriority.TOP_LEVEL}
                  component={Routes.MostRecommended}
                />
                {/* Root route */}
                <PageKeyRoute
                  pageKey="home"
                  priority={PageKeyPriority.TOP_LEVEL}
                  path="/"
                  exact={true}
                  component={Routes.Home}
                />
                {/* 404 */}
                <Route component={NotFound} />
              </Switch>
            </ChatProvider>
          </CartProvider>
        )}
      />
    </Switch>
  )
}

export default withRouter(Router)
